"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { getApiErrorStatus } from "@/shared/api/error-handler";

import {
  fetchBracketByTournamentId,
  updateMatchScore,
  type UpdateMatchScoreRequest,
  removeTeamFromTournament,
  addTeamToTournament,
  fetchTournamentPermissions,
  swapTeamsInMatch,
  type SwapTeamsInMatchRequest,
  swapTeamsBetweenMatches,
  type SwapTeamsBetweenMatchesRequest,
  moveTeamToMatch,
  type MoveTeamToMatchRequest,
  reshuffleBracket,
  publishBracket,
  freezeBracket,
  unfreezeBracket,
  markMatchLive,
  unliveMatch,
  pauseMatch,
  resumeMatch,
  type MatchLifecycleRequest,
} from "./client";
import type { ApiBracketDTO } from "./types";
import { tournamentKeys } from "@/shared/api/query-keys";

/**
 * Страховочный интервал опроса сетки.
 *
 * KAN-935, решение владельца 03.09.2026. Основной источник обновлений теперь —
 * события матчей из хаба `match-live` (`useBracketLiveEvents`), но опрос не
 * убирается совсем: сегодня же чинили KAN-919, где события молча терялись по
 * пути. Довериться событиям без страховки — значит сделать потерю невидимой:
 * человек смотрел бы на устаревшую сетку и не узнал об этом. Две с половиной
 * минуты — компромисс: в десять раз дешевле прежних 15 секунд, но «молча
 * устарело навсегда» превращается в «отстало максимум на две с половиной минуты».
 *
 * Для анонима это вообще единственный источник: хаб под `[Authorize]`.
 */
export const BRACKET_SAFETY_REFETCH_MS = 150_000;

/**
 * @param options.isFinished турнир завершён/отменён — опрос выключается совсем.
 *   Отыгранная сетка не меняется, а ответ у неё самый тяжёлый на странице
 *   (24,6 КБ по замеру): до правки вкладка «Итоги» качала их каждые 15 секунд.
 */
export function useBracket(
  tournamentId: string,
  options?: { isFinished?: boolean },
) {
  const isFinished = options?.isFinished === true;
  return useQuery<ApiBracketDTO, Error>({
    queryKey: ["bracket", tournamentId],
    queryFn: () => fetchBracketByTournamentId(tournamentId),
    enabled: Boolean(tournamentId),
    retry: (failureCount, error: unknown) => {
      // Don't retry 404s
      if (getApiErrorStatus(error) === 404) return false;
      return failureCount < 3;
    },
    refetchInterval: isFinished ? false : BRACKET_SAFETY_REFETCH_MS,
    refetchIntervalInBackground: false,
    // При страховочном интервале в 2,5 минуты возврат на вкладку обязан
    // догонять данные сам — иначе экономия на опросе оплачена устаревшей
    // сеткой. Глобальный дефолт проекта (refetchOnMount/OnWindowFocus: false)
    // рассчитан на 15-секундный опрос, которого здесь больше нет.
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });
}

export function useUpdateMatchScore(tournamentId: string) {
  const qc = useQueryClient();
  return useMutation<
    void,
    Error,
    { matchId: string; payload: UpdateMatchScoreRequest },
    { prev: ApiBracketDTO | undefined }
  >({
    mutationFn: ({ matchId, payload }) =>
      updateMatchScore(tournamentId, matchId, payload),
    onMutate: async ({ matchId, payload }) => {
      const key = ["bracket", tournamentId] as const;
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<ApiBracketDTO>(key);
      if (prev) {
        const next: ApiBracketDTO = {
          ...prev,
          matches: prev.matches.map((m) =>
            m.id === matchId
              ? {
                  ...m,
                  ...(payload.scoreA !== undefined
                    ? { scoreA: payload.scoreA }
                    : {}),
                  ...(payload.scoreB !== undefined
                    ? { scoreB: payload.scoreB }
                    : {}),
                }
              : m,
          ),
        };
        qc.setQueryData<ApiBracketDTO>(key, next);
      }
      return { prev };
    },
    onError: (_e, _vars, context) => {
      if (context?.prev)
        qc.setQueryData<ApiBracketDTO>(
          ["bracket", tournamentId] as const,
          context.prev,
        );
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["bracket", tournamentId] as const });
    },
  });
}

export function useRemoveTeam(tournamentId: string) {
  const qc = useQueryClient();
  return useMutation<
    void,
    Error,
    { teamId: string },
    { prev: ApiBracketDTO | undefined }
  >({
    mutationFn: ({ teamId }) => removeTeamFromTournament(tournamentId, teamId),
    onMutate: async ({ teamId }) => {
      const key = ["bracket", tournamentId] as const;
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<ApiBracketDTO>(key);
      if (prev) {
        qc.setQueryData<ApiBracketDTO>(key, {
          ...prev,
          teams: prev.teams.filter((t) => t.id !== teamId),
        });
      }
      return { prev };
    },
    onError: (_e, _vars, ctx) => {
      if (ctx?.prev)
        qc.setQueryData<ApiBracketDTO>(
          ["bracket", tournamentId] as const,
          ctx.prev,
        );
    },
    onSettled: () =>
      qc.invalidateQueries({ queryKey: ["bracket", tournamentId] as const }),
  });
}

export function useAddTeam(tournamentId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, { teamId: string }>({
    mutationFn: ({ teamId }) => addTeamToTournament(tournamentId, teamId),
    onSettled: () =>
      qc.invalidateQueries({ queryKey: ["bracket", tournamentId] as const }),
  });
}

export function useTournamentPermissions(tournamentId: string) {
  return useQuery({
    queryKey: tournamentKeys.permissions(tournamentId),
    queryFn: () => fetchTournamentPermissions(tournamentId),
    enabled: Boolean(tournamentId),
  });
}

export function useCanEditTournament(tournamentId: string, userRole?: string) {
  const { data: perms, isLoading } = useTournamentPermissions(tournamentId);
  const canByApi = Boolean(perms?.canEditBracket);
  const canByRole = userRole === "Organizer";
  // Различаем «права ещё грузятся» и «доступ запрещён»: пока запрос в полёте,
  // canEdit=false — но это НЕ отказ. Вызывающий обязан ждать !isLoading, иначе
  // легитимного организатора выкинет с редактора при пустом кэше (KAN-158).
  return { canEdit: canByApi || canByRole, isLoading };
}

export function useSwapTeamsInMatch(tournamentId: string) {
  const qc = useQueryClient();
  return useMutation<
    void,
    Error,
    { matchId: string; expectedUpdatedAt: string },
    { prev: ApiBracketDTO | undefined }
  >({
    mutationFn: ({ matchId, expectedUpdatedAt }) =>
      swapTeamsInMatch(matchId, {
        expectedUpdatedAt,
      } satisfies SwapTeamsInMatchRequest),
    onMutate: async ({ matchId }) => {
      const key = ["bracket", tournamentId] as const;
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<ApiBracketDTO>(key);
      if (prev) {
        const next: ApiBracketDTO = {
          ...prev,
          matches: prev.matches.map((m) =>
            m.id === matchId
              ? {
                  ...m,
                  teamAId: m.teamBId,
                  teamBId: m.teamAId,
                }
              : m,
          ),
        };
        qc.setQueryData<ApiBracketDTO>(key, next);
      }
      return { prev };
    },
    onError: (_e, _vars, context) => {
      if (context?.prev)
        qc.setQueryData<ApiBracketDTO>(
          ["bracket", tournamentId] as const,
          context.prev,
        );
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["bracket", tournamentId] as const });
    },
  });
}

export function useMoveTeamToMatch(tournamentId: string) {
  const qc = useQueryClient();
  return useMutation<
    void,
    Error,
    MoveTeamToMatchRequest,
    { prev: ApiBracketDTO | undefined }
  >({
    mutationFn: (payload) => moveTeamToMatch(payload),
    onMutate: async ({ teamId, fromMatchId, toMatchId }) => {
      const key = ["bracket", tournamentId] as const;
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<ApiBracketDTO>(key);
      if (prev) {
        const next: ApiBracketDTO = {
          ...prev,
          matches: prev.matches.map((m) => {
            if (m.id === fromMatchId) {
              return {
                ...m,
                teamAId: m.teamAId === teamId ? undefined : m.teamAId,
                teamBId: m.teamBId === teamId ? undefined : m.teamBId,
              };
            }
            if (m.id === toMatchId) {
              const hasTeamA = Boolean(m.teamAId);
              const hasTeamB = Boolean(m.teamBId);
              if (!hasTeamA) {
                return { ...m, teamAId: teamId };
              }
              if (!hasTeamB) {
                return { ...m, teamBId: teamId };
              }
            }
            return m;
          }),
        };
        qc.setQueryData<ApiBracketDTO>(key, next);
      }
      return { prev };
    },
    onError: (_e, _vars, context) => {
      if (context?.prev)
        qc.setQueryData<ApiBracketDTO>(
          ["bracket", tournamentId] as const,
          context.prev,
        );
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["bracket", tournamentId] as const });
    },
  });
}

export function useSwapTeamsBetweenMatches(tournamentId: string) {
  const qc = useQueryClient();
  return useMutation<
    void,
    Error,
    SwapTeamsBetweenMatchesRequest,
    { prev: ApiBracketDTO | undefined }
  >({
    mutationFn: (payload) => swapTeamsBetweenMatches(payload),
    onMutate: async ({ fromMatchId, fromSlot, toMatchId, toSlot }) => {
      const key = ["bracket", tournamentId] as const;
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<ApiBracketDTO>(key);
      if (!prev) return { prev };

      const get = (m: ApiBracketDTO["matches"][0], slot: "A" | "B") =>
        slot === "A" ? m.teamAId : m.teamBId;
      const set = (
        m: ApiBracketDTO["matches"][0],
        slot: "A" | "B",
        v: string | undefined,
      ) => (slot === "A" ? { ...m, teamAId: v } : { ...m, teamBId: v });

      const from = prev.matches.find((m) => m.id === fromMatchId);
      const to = prev.matches.find((m) => m.id === toMatchId);
      if (!from || !to) return { prev };

      const fromTeam = get(from, fromSlot);
      const toTeam = get(to, toSlot);
      if (!fromTeam || !toTeam) return { prev };

      const next: ApiBracketDTO = {
        ...prev,
        matches: prev.matches.map((m) => {
          if (m.id === fromMatchId) return set(m, fromSlot, toTeam);
          if (m.id === toMatchId) return set(m, toSlot, fromTeam);
          return m;
        }),
      };
      qc.setQueryData<ApiBracketDTO>(key, next);
      return { prev };
    },
    onError: (_error, _variables, context) => {
      if (context?.prev) {
        qc.setQueryData<ApiBracketDTO>(
          ["bracket", tournamentId] as const,
          context.prev,
        );
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["bracket", tournamentId] as const });
    },
  });
}

export function useReshuffleBracket(tournamentId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, void>({
    mutationFn: () => reshuffleBracket(tournamentId),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["bracket", tournamentId] as const });
    },
  });
}

export function usePublishBracket(tournamentId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, void>({
    mutationFn: () => publishBracket(tournamentId),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["bracket", tournamentId] as const });
    },
  });
}

// Заморозка/разморозка сетки (Draft ↔ Frozen). Эндпоинты:
// POST /tournaments/{id}/brackets/freeze | /unfreeze. По образцу usePublishBracket.
export function useFreezeBracket(tournamentId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, void>({
    mutationFn: () => freezeBracket(tournamentId),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["bracket", tournamentId] as const });
    },
  });
}

export function useUnfreezeBracket(tournamentId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, void>({
    mutationFn: () => unfreezeBracket(tournamentId),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["bracket", tournamentId] as const });
    },
  });
}

export function useMarkMatchLive(tournamentId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, { matchId: string; payload?: MatchLifecycleRequest }>({
    mutationFn: ({ matchId, payload }) => markMatchLive(matchId, payload),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["bracket", tournamentId] as const });
    },
  });
}

export function useUnliveMatch(tournamentId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, { matchId: string; payload?: MatchLifecycleRequest }>({
    mutationFn: ({ matchId, payload }) => unliveMatch(matchId, payload),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["bracket", tournamentId] as const });
    },
  });
}

export function usePauseMatch(tournamentId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, { matchId: string; payload?: MatchLifecycleRequest }>({
    mutationFn: ({ matchId, payload }) => pauseMatch(matchId, payload),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["bracket", tournamentId] as const });
    },
  });
}

export function useResumeMatch(tournamentId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, { matchId: string; payload?: MatchLifecycleRequest }>({
    mutationFn: ({ matchId, payload }) => resumeMatch(matchId, payload),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["bracket", tournamentId] as const });
    },
  });
}
